// Customer addresses (legacy "Addresses" modal):
//   GET customers/addresses/{id} · POST customers/add_address ·
//   PUT customers/update_address/{address} · DELETE customers/delete_address/{address}
import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { adminApi, apiErrorMessage, apiValidationErrors, SelectOption } from '../../api/client';
import { LoadingBlock } from '../../components/StateBlock';
import { t } from '../../i18n';
import { fetchAdaptedOptions } from './helpers';

type Address = Record<string, unknown> & { id?: string };

const FIELDS: Array<{ key: string; label: string; required?: boolean }> = [
  { key: 'first_name', label: 'field.firstName', required: true },
  { key: 'last_name', label: 'field.lastName', required: true },
  { key: 'email', label: 'field.email' },
  { key: 'phone', label: 'field.phone', required: true },
  { key: 'company', label: 'field.company' },
  { key: 'house_no', label: 'field.houseNo', required: true },
  { key: 'street_no', label: 'field.streetNo', required: true },
  { key: 'address', label: 'field.address', required: true },
  { key: 'city', label: 'field.city', required: true },
  { key: 'postal_code', label: 'field.postalCode', required: true }
];

const emptyAddress = (): Address => ({ first_name: '', last_name: '', email: '', phone: '', company: '', house_no: '', street_no: '', address: '', city: '', postal_code: '', country: '', state: '', default: 0 });

export function AddressesDialog({ customerId, startWithForm = false, onClose }: { customerId: string | number; startWithForm?: boolean; onClose: () => void }) {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<Address | null>(startWithForm ? emptyAddress() : null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState<SelectOption[]>([]);
  const [states, setStates] = useState<SelectOption[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApi.get(`customers/addresses/${customerId}`);
      setAddresses(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setMessage({ text: apiErrorMessage(err, t('parties.addressesUnavailable')), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
    fetchAdaptedOptions('countries').then(setCountries).catch(() => setCountries([]));
  }, [load]);

  const country = form ? String(form.country ?? '') : '';
  useEffect(() => {
    if (!country) {
      setStates([]);
      return;
    }
    fetchAdaptedOptions(`states?country=${encodeURIComponent(country)}`).then(setStates).catch(() => setStates([]));
  }, [country]);

  const set = (key: string, value: unknown) => setForm((current) => (current ? { ...current, [key]: value } : current));

  const save = async () => {
    if (!form) return;
    setSaving(true);
    setErrors({});
    setMessage(null);
    try {
      const payload = { ...form, customer_id: customerId, default: form.default ? 1 : 0 };
      const response = form.id ? await adminApi.put(`customers/update_address/${form.id}`, payload) : await adminApi.post('customers/add_address', payload);
      if (response.data?.success === false) throw new Error(t('common.saveFailed'));
      setForm(null);
      setMessage({ text: form.id ? t('parties.recordUpdated') : t('parties.recordAdded'), severity: 'success' });
      await load();
    } catch (err) {
      const validation = apiValidationErrors(err);
      setErrors(validation);
      if (!Object.keys(validation).length) setMessage({ text: apiErrorMessage(err, t('common.saveFailed')), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!form?.id) return;
    setSaving(true);
    try {
      const response = await adminApi.delete(`customers/delete_address/${form.id}`);
      if (!response.data?.success) throw new Error(t('common.deleteFailed'));
      setForm(null);
      setMessage({ text: t('parties.recordDeleted'), severity: 'success' });
      await load();
    } catch (err) {
      setMessage({ text: apiErrorMessage(err, t('common.deleteFailed')), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const err = (key: string) => errors[key]?.[0];

  return (
    <Stack spacing={2} sx={{ minWidth: { sm: 520 } }}>
      {message ? <Alert severity={message.severity}>{message.text}</Alert> : null}
      {form ? (
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">{form.id ? t('parties.editAddress') : t('parties.addAddress')}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            {FIELDS.map((field) => (
              <TextField
                key={field.key}
                size="small"
                label={t(field.label)}
                required={field.required}
                value={String(form[field.key] ?? '')}
                onChange={(e) => set(field.key, e.target.value)}
                error={Boolean(err(field.key))}
                helperText={err(field.key)}
              />
            ))}
            <TextField select size="small" required label={t('field.country')} value={country} onChange={(e) => setForm((current) => (current ? { ...current, country: e.target.value, state: '' } : current))} error={Boolean(err('country'))} helperText={err('country')}>
              {countries.map((option) => (
                <MenuItem key={String(option.value)} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" required label={t('field.state')} value={String(form.state ?? '')} onChange={(e) => set('state', e.target.value)} error={Boolean(err('state'))} helperText={err('state')}>
              {states.map((option) => (
                <MenuItem key={String(option.value)} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <FormControlLabel control={<Checkbox checked={Boolean(form.default)} onChange={(e) => set('default', e.target.checked ? 1 : 0)} />} label={t('parties.defaultAddress')} />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {form.id ? (
              <Button color="error" disabled={saving} onClick={() => void remove()}>
                {t('action.delete')}
              </Button>
            ) : null}
            <Button disabled={saving} onClick={() => setForm(null)}>
              {t('action.cancel')}
            </Button>
            <Button variant="contained" disabled={saving} onClick={() => void save()}>
              {t('action.save')}
            </Button>
          </Stack>
          <Divider />
        </Stack>
      ) : null}
      {loading ? (
        <LoadingBlock />
      ) : addresses.length ? (
        <Stack spacing={1}>
          {addresses.map((address) => (
            <Box
              key={String(address.id)}
              role="button"
              onClick={() => setForm({ ...emptyAddress(), ...address })}
              sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {String(address.first_name ?? '')} {String(address.last_name ?? '')}
                {address.company ? ` (${String(address.company)})` : ''}
              </Typography>
              <Typography variant="caption" display="block">
                {[address.house_no, address.street_no, address.address, address.city, address.postal_code, address.state_name, address.country_name].filter(Boolean).join(' ')}
              </Typography>
              <Typography variant="caption" display="block">
                {[address.phone, address.email].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('parties.noData')}
        </Typography>
      )}
      <Stack direction="row" spacing={1} justifyContent="space-between">
        <Button disabled={Boolean(form)} onClick={() => setForm(emptyAddress())}>
          {t('parties.addAddress')}
        </Button>
        <Button onClick={onClose}>{t('action.close')}</Button>
      </Stack>
    </Stack>
  );
}
